import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useCompaniesTableStore } from "@/stores/useTableStore";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import {
  COMPANIES_ACTIONS,
  CompaniesHierarchyDataDto,
  GeneralDialogProps,
  ROUTES
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import DialogTitleWithTooltip from "../../../../../Dialog/components/DialogTitleWithTooltip/DialogTitleWithTooltip";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogHeight } from "../../../../../Dialog/types/enums";
import useCompanies from "../../../../../Navbar/components/CompanySelector/hooks/useCompanies";
import { EditCompanyPayloadType } from "../../../../types";
import useEditItem from "../../../inlineActions/hooks/useEditItem";
import EditCompanyDialogContent from "./EditCompanyDialogContent";

interface IEditCompanyDialogProps {
  rowData: CompaniesHierarchyDataDto;
}

const EditCompanyDialog = ({
  isOpen,
  onClose,
  rowData
}: IEditCompanyDialogProps & GeneralDialogProps) => {
  const { t } = useTranslation();
  const { editItem } = useEditItem();

  const { getQueryData } = useCompanies(undefined, true);
  const { user, getUserData, tokenInfo } = useAuth();

  const { detailsData } = useCompaniesTableStore();

  const [isReadyToConfirm, setIsReadyToConfirm] = useState<boolean | "" | null>(
    false
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [payload, setPayload] = useState<EditCompanyPayloadType>({
    id: null,
    name: null,
    comment: null
  });

  const editableItemHasOpenedDetails = detailsData?.id === payload.id;

  const resourceString = t("General##company");
  const isReadyToCallApi = isReadyToConfirm;

  const handleCloseEditCompanyDialog = () => {
    onClose(COMPANIES_ACTIONS.EditCompany);
  };

  const handleSubmit = async () => {
    if (isReadyToCallApi) {
      await editItem(
        setIsLoading,
        ROUTES.Companies,
        payload,
        null, // companies page does not have search field
        editableItemHasOpenedDetails,
        resourceString,
        handleCloseEditCompanyDialog
      );

      if (user?.selectedCompany.id === payload.id) {
        tokenInfo && (await getUserData());
      }

      await getQueryData();
    }
  };

  return (
    <Dialog
      data-testid="edit-company-dialog"
      title={
        rowData &&
        ((
          <DialogTitleWithTooltip
            title={t("Dialog##edit company", {
              name: rowData.name
            })}
            tooltipText={rowData.name}
          />
        ) as any)
      }
      extendToMaxWidth
      maxHeight={DialogHeight.MaxHeight}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleCloseEditCompanyDialog}
      submit={handleSubmit}
      actions={
        <DialogActions
          onClose={handleCloseEditCompanyDialog}
          isLoading={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="edit-company-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-edit-company"
        />
      }
    >
      {rowData && (
        <EditCompanyDialogContent
          rowData={rowData}
          setPayload={setPayload}
          setIsReadyToConfirm={setIsReadyToConfirm}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

export default EditCompanyDialog;
